import 'audioworklet-polyfill';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';

import useMicPermission from '../../hooks/useMicPermission';
import useNetwork from '../../hooks/useNetwork';

import {
  captureMicrophoneExceptionIfExists,
  isBluetoothMic,
  isMicAccessDenied,
  stopStreams,
  trackMicPermissionIfPromptExists,
} from '../../helpers/audioRecorder';
import {
  destroyRecordingContext,
  getAvailableMicsFromMediaDevices,
  getMediaDevices,
  getMicIdToSelect,
  startRecording,
  stopRecording,
} from '../../utils/audioRecorder';
import { getSnack } from '../../utils/snackbar';

import AudioRecorderUI from './AudioRecorderUI';
import { ipcRendererWithDeregistering } from '../../utils/electron';
import { hasScribe } from '../../utils/status';
import uniqBy from 'lodash/uniqBy';

const AudioRecorder = props => {
  const {
    addNotification,
    availableMics,
    audioParamsSent,
    conversationEnded,
    curseFilter,
    electronCaptionMode,
    hasMicrophoneRestarted,
    isMuted,
    isRestartingMicrophone,
    lang,
    micIdSelected,
    onlyInternalAudio,
    participants: unfilteredParticipants,
    recording: isMicrophoneActive,
    recordingContext,
    resetAudioRecorderState,
    resetRecordingContext,
    selectedCaptions,
    setAvailableMics,
    setHasMicrophoneRestarted,
    setIsRestartingMicrophone,
    setMicSelected,
    setRecordingContext,
    showExpandedMic,
    speechLang,
    status,
    startRecord: activateMicrophone,
    stopRecord: deactivateMicrophone,
    t,
    theme,
    user,
  } = props;

  const isOnline = useNetwork();
  const participants = uniqBy(unfilteredParticipants || [], 'avaId').filter(p => !p.scribe);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { micPermissionStatus, trackMicPermissionPrompt } = useMicPermission();

  const [microphoneLoading, setMicrophoneLoading] = useState(true);
  // We add 'isRecording' state variable, because we want to decouple app-wide
  // 'recording' state changes (managed by startRecord() and stopRecord()
  // actions) with actual audio recording process. The reason is that setting up
  // and tearing down audio recording is a heavy/async process and random calls to start
  // or stop recording in the codebase can casue race conditions in this component.
  // Having a separate state variable gives us the ability to gracefully manage
  // this process.
  const [isRecording, setIsRecording] = useState(false);
  const [micAccessDenied, setMicAccessDenied] = useState(false);
  const [volume, setVolume] = useState(0);
  const [errorShown, setErrorShown] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);

  const handleMicrophoneError = err => {
    deactivateMicrophone();
    if (isMicAccessDenied(err)) {
      setMicAccessDenied(true);
    }

    trackMicPermissionIfPromptExists(err, trackMicPermissionPrompt);
    captureMicrophoneExceptionIfExists(err, t, errorShown, setErrorShown, errorMessage => {
      getSnack({ enqueueSnackbar, closeSnackbar })(errorMessage, {
        variant: 'error',
        preventDuplicate: true,
        autoHideDuration: 10000,
      });
    });
  };

  const retrieveAndSetAvailableMics = async () => {
    try {
      const mediaDevices = await getMediaDevices();
      const currentAvailableMics = getAvailableMicsFromMediaDevices(mediaDevices, { isMuted, participants, t });
      setAvailableMics(currentAvailableMics);

      // When the component loads, available mics will be empty initially.
      // Checking microphoneLoading lets the audio devices load first.
      if (!microphoneLoading && !availableMics.length) {
        throw new Error('no audio input');
      }
    } catch (err) {
      handleMicrophoneError(err);
    }
  };

  const onDeviceChange = async () => await retrieveAndSetAvailableMics();

  useEffect(() => {
    (async () => await retrieveAndSetAvailableMics())();

    navigator.mediaDevices.ondevicechange = onDeviceChange;
    if (window.isElectron) {
      ipcRendererWithDeregistering.addEventListener('multiOutputChanged', onDeviceChange);
    }

    return () => {
      navigator.mediaDevices.ondevicechange = null;
      if (window.isElectron) {
        ipcRendererWithDeregistering.removeEventListener('multiOutputChanged', onDeviceChange);
      }

      resetAudioRecorderState();
    };
  }, []);

  useEffect(() => {
    if (availableMics.length) {
      let micIdToSelect = getMicIdToSelect(availableMics, micIdSelected) || '';

      const defaultMic = availableMics.find(mic => mic.default);
      if (participants.length > 1 && defaultMic) {
        micIdToSelect = defaultMic.value;

        // When the component first loads and this is a group convo, we make sure
        // the Ava Mic is NOT available. If it is available, we reload the list
        // of available mics again.
        const enabledAvaMic = availableMics.find(mic => mic.label.includes('Ava Computer Audio'));
        if (enabledAvaMic) {
          onDeviceChange();
          return;
        }
      }

      setMicSelected(micIdToSelect);
      setMicrophoneLoading(false);
    }
  }, [availableMics]);

  useEffect(() => {
    if (isRestartingMicrophone) {
      stopRecording(setRecordingContext);
      setHasMicrophoneRestarted(true);
      setIsRestartingMicrophone(false);
      return;
    }

    if (isRecording && isOnline) {
      (async () => {
        // Firefox has a bug with concurrrent microphones. Therefore we need
        // to stop previous tracks before starting a new microphone.
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1238038
        if (recordingContext) {
          const { streams } = recordingContext;
          stopStreams(streams);
          setIsPermissionGranted(false);
        }

        await startRecording(
          { ...props, setIsPermissionGranted, setVolume, trackMicPermissionPrompt },
          setRecordingContext,
          handleMicrophoneError
        );

        if (hasMicrophoneRestarted) {
          setHasMicrophoneRestarted(false);
        }
      })();
    } else {
      stopRecording(resetRecordingContext);
    }
  }, [micIdSelected, isRecording, isRestartingMicrophone]);

  useEffect(() => {
    if (isPermissionGranted) {
      onDeviceChange();
    }
  }, [isPermissionGranted]);

  useEffect(() => {
    setIsRecording(isMicrophoneActive);
  }, [isMicrophoneActive]);

  useEffect(() => {
    if (isMicrophoneActive) {
      setIsRecording(isOnline ? true : false);
    }
  }, [isOnline]);

  useEffect(() => {
    if (micAccessDenied && micPermissionStatus === 'granted') {
      // User granted mic access after previously denying it,
      // so we try to start recording again.
      activateMicrophone();
    }
  }, [micPermissionStatus]);

  useEffect(() => {
    if (!microphoneLoading) {
      setIsRestartingMicrophone(true);
    }
  }, [onlyInternalAudio]);

  useEffect(() => {
    const convoSettingChanged = curseFilter || lang || speechLang;
    const isMicOn = isRecording && audioParamsSent;

    if (convoSettingChanged && isMicOn) {
      setIsRestartingMicrophone(true);
    }
  }, [curseFilter, lang, speechLang]);

  useEffect(() => {
    if (!microphoneLoading && !isMuted) {
      const enabledAvaMic = availableMics.find(mic => mic.label.includes('Ava Computer Audio'));

      if (participants.length > 1) {
        // We make the Ava Mic forbidden when there is more than one
        // participant. So we reload the devices again. To prevent the call from
        // happening every time a new participant joins, we only reload devices
        // when the Ava Mic is available.
        if (enabledAvaMic) {
          onDeviceChange();
        }
      } else if (participants.length < 2) {
        // If this turns into a solo convo, we reload devices again because the
        // Ava Mic will be available now.
        onDeviceChange();
      }
    }
  }, [participants.length]);

  useEffect(() => {
    // Conversation's audio streams can be updated remotely and backend sends this info
    // via 'room-status-update' message. Here we are interested if our audio has been
    // muted remotely.
    //
    // There could be more than one stream for each participant. We check the last (latest)
    // for this user.
    const myStreams = (status.audioStreams || []).filter(stream => stream.id === user.avaId);
    if (myStreams.length > 0) {
      const [last] = myStreams.slice(-1);
      if (last.forceMuted && isMicrophoneActive) {
        deactivateMicrophone();
        if (window.isElectron && electronCaptionMode) {
          addNotification({
            text: t('conversation.notifications.youHaveBeenMuted'),
            timestamp: Date.now(),
            type: 'default',
          });
        } else {
          getSnack(props)(t('conversation.notifications.youHaveBeenMuted'));
        }
      }
    }
  }, [status.audioStreams]);

  useEffect(() => {
    return async () => {
      await destroyRecordingContext(recordingContext);
    };
  }, [recordingContext]);

  useEffect(() => {
    if (conversationEnded && isRecording) {
      deactivateMicrophone();
    }
  }, [conversationEnded]);

  /*
  This logic is partially copied from AudioRecorderUI. The purpose of putting it
  here is to reduce the number of React state changed when `volume` changes.
  `volume` is updated 5-8 times a second, and reducing the size of the tree
  that is updated as that happens helps performance.
   */
  const showMinimalMic = window.isElectron && electronCaptionMode && !showExpandedMic;
  let shadowColor;
  if (isRecording && audioParamsSent) {
    if (hasScribe(status)) {
      shadowColor = theme.palette.ava.green;
    } else if (selectedCaptions === undefined || selectedCaptions === 'premium') {
      shadowColor = theme.palette.ava.gradientBlue;
      shadowColor = theme.palette.ava.darkBlue;
    } else {
      shadowColor = theme.palette.ava.blue;
    }
  }
  // Volume is currently between 0 and 128, but this is browser-dependent
  const shadowSpread = Math.min(8, Math.floor(Math.log2(volume) * 3));
  const width = showMinimalMic ? 20 : 56;
  const height = showMinimalMic ? 20 : 56;
  const positionLeft = window.isElectron && electronCaptionMode ? `calc(50% - ${width / 2}px)` : 0;
  const positionTop = window.isElectron && electronCaptionMode ? `calc(50% - ${height / 2 - 1}px)` : 0;

  return (
    <div>
      <div
        style={{
          position: 'absolute',
          left: positionLeft,
          top: positionTop,
          height,
          width,
          minHeight: height,
          borderRadius: '50%',
          transition: 'all 120ms ease-in 0s, box-shadow 250ms ease-in 0s',
          boxShadow: isRecording ? `0px 0px 5px ${shadowSpread}px ${shadowColor}90` : 'black 0px 0px 0px 0px',
        }}
      ></div>
      <AudioRecorderUI
        {...props}
        showMinimalMic={showMinimalMic}
        isRecording={isRecording && isOnline}
        bluetooth={isBluetoothMic(availableMics.find(mic => mic.value === micIdSelected))}
        microphoneDenied={micAccessDenied}
      />
    </div>
  );
};

export default AudioRecorder;
